// Angular
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Models
import { Job, JobLink, JobOptions } from '../../models';
import { AppCleanup, DynamicMenuButton, DynamicTableColumn, DynamicTableType } from '../../../../shared/models';
import { takeUntil } from 'rxjs';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Services
import { JobService } from '../../services/job.service';
import { BrowserService, CoreService } from '../../../../shared/services';
import { DialogService } from '../../../../shared/services/dialog.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Components
import { NewJobLinkDialogComponent } from '../new-job-link-dialog/new-job-link-dialog.component';

// Libraries
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { orderBy } from 'lodash-es';
import { formatInTimeZone } from 'date-fns-tz';

// Enum
import { StatusType } from '../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';
import { TimezoneService } from '../../services/timezone.service';
import { SpinnerSizeType } from '../../../../shared/components';

@Component({
	selector: 'job-links-dialog',
	templateUrl: './job-links-dialog.component.html',
	styleUrls: ['job-links-dialog.component.scss'],
})
export class JobLinksDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	protected readonly SpinnerSizeType = SpinnerSizeType;
	
	private readonly dateFormat: string = 'M/d/yyyy, h:mm a';
	
	jobOffset: number = new Date().getTimezoneOffset() / -60; // default to browser timezone offset
	jobLinks: JobLink[];
	columnList: DynamicTableColumn[] = [
		{
			name: 'generatedDateDisplay',
			display: 'Generated',
			width: 1.5,
		},
		{
			name: 'expirationDateDisplay',
			display: 'Expiration',
			width: 1.5,
		},
		{
			name: 'createdBy',
			display: 'Created By',
			width: 2,
		},
		{
			name: 'comments',
			display: 'Comments',
			width: 4,
		},
		{
			name: 'status',
			display: 'Status',
			type: DynamicTableType.HTML,
			width: 1,
		}
	];

	menuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'file_copy',
			name: 'Copy',
			event: this.copyLink.bind(this),
		},
		{
			matIcon: 'tab',
			name: 'Open in a new tab',
			event: this.openInNewTab.bind(this),
		},
		{
			matIcon: 'launch',
			name: 'Open in a new window',
			event: this.openInNewWindow.bind(this),
		},
		{
			matIcon: 'link_off',
			name: 'Expire link',
			event: this.expireLink.bind(this),
		},
	];
	isTableDataLoaded: boolean = false;
	linkActive: boolean = false;
	job: Job;
	jobLinkUrl: string;
	showSpinner: boolean = false;

	constructor(
		public dialog: MatDialog,
		public jobService: JobService,
		private coreService: CoreService,
		private browser: BrowserService,
		@Inject(MAT_DIALOG_DATA) public data: { key: string },
		public dialogRef: MatDialogRef<JobLinksDialogComponent>,
		private translateService: TranslateService,
		private timezoneService: TimezoneService,
		private dialogService: DialogService,
		private sanitizer: DomSanitizer,
		private spinnerService: SpinnerService
	) {
		super();
		let root = window.location.origin;
		this.jobLinkUrl = `${root}/job/link`;
	}

	ngOnInit() {
		// Get job from cache.
		this.job = new Job(this.jobService.findJob(this.data.key));
		this.linkActive = this.job.jobLinksActive;

		this.jobService
			.getJobLinks(this.job.key)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {
				this.jobOffset = this.timezoneService.getJobOffset(this.job.utcDateTimeOffset);
				this.updateJobLinksDates();
				this.isTableDataLoaded = true;
			});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}
	
	copyLink(link: JobLink) {
		const result = this.browser.copyTextToClipboard(`${this.jobLinkUrl}/${link.key}`);
		if (result) {
			const message = `${this.translateService.instant('GENERIC.COPIED', { name: this.translateService.instant('MODULE.JOB.LINK.TITLE_CAPITAL') })}`;
			this.coreService.toast({ message: message, type: StatusType.SUCCESS });
		}
	}

	openInNewTab(link: JobLink) {
		const url = `/job/link/${link.key}`;
		UtilityHelper.openInNewTab(url);
	}

	openInNewWindow(link: JobLink) {
		const url = `/job/link/${link.key}`;
		UtilityHelper.openInNewWindow(url);
	}
	
	expireLink(link: JobLink) {
		this.showSpinner = true;
		setTimeout(() => {
			const { key } = link;
			const jobId = this.job.id;
			this.jobService
				.expireLink({ key, jobId })
				.pipe(takeUntil(this.destroy))
				.subscribe((expiredLink: JobLink) => {
					this.updateExistingJobLink(expiredLink);
					setTimeout(() => {
						this.showSpinner = false;
						const message = this.translateService.instant('MODULE.JOB.LINK_EXPIRED');
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}, 350);
				});
		}, 1);
	}

	toggleChange() {
		if (this.linkActive) {
			// If trying to enable
			this.toggleSlider();
		} else {
			// If trying to disable
			const confirmModel: ConfirmDialogModel = {
				title: 'Deactivate links',
				message: 'Are you sure you want to deactivate the job links?',
				action: 'Deactivate',
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						this.toggleSlider();
					} else {
						this.linkActive = true;
					}
				});
		}
	}
	
	updateJobLinksDates() {
		let jobLinks: JobLink[] = this.jobService.jobLinks || [];
		
		// Adjusting dates retrieved from server to display in correct timezone based on the job offset.
		// On page-load, the GET call returns the date values in string UTC format, so they need converted.
		if (jobLinks?.length && this.timezoneService.isValidUtcOffset(this.jobOffset)) {
			for (let jobLink of jobLinks) {
				if (jobLink.generatedDate) {
					jobLink.generatedDateDisplay = this.convertAndFormatForDisplay(jobLink.generatedDate);
				}
				
				if (jobLink.expirationDate) {
					jobLink.expirationDateDisplay = this.convertAndFormatForDisplay(jobLink.expirationDate);
				}
				
				jobLink.status = this.getStatus(jobLink);
			}
		}
		
		this.jobLinks = this.getSortedJobLinks(jobLinks);
	}

	createLink() {
		const dialogRef = this.dialog.open(NewJobLinkDialogComponent, {
			data: {
				job: this.job,
			},
			width: '100%',
			maxWidth: '400px',
			panelClass: 'newJobLinkListModal',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data) => {
				if (data) {
					this.spinnerService.showAppSpinner();
					this.jobService
						.generateLink(data, this.job.key)
						.pipe(takeUntil(this.destroy))
						.subscribe(
							(jobLink: JobLink) => {
								this.spinnerService.hideAppSpinner();
								this.processNewJobLink(jobLink);
							},
							() => {
								this.spinnerService.hideAppSpinner();
							}
						);
				}
			});
	}
	
	private processNewJobLink(jobLink: JobLink) {
		if (jobLink) {
			jobLink.status = this.getStatus(jobLink) as any;
			const generatedDateDisplay: string = this.convertAndFormatForDisplay(jobLink.generatedDate);
			const expirationDateDisplay: string = this.convertAndFormatForDisplay(jobLink.expirationDate);
			const link = { ...jobLink, generatedDateDisplay, expirationDateDisplay };
			this.jobService.jobLinks.push(link);
			this.jobLinks = this.getSortedJobLinks([...this.jobLinks, link]);
			
			const url: string = `${this.jobLinkUrl}/${jobLink.key}`;
			
			let isCopiedToClipboard: boolean = this.browser.copyTextToClipboard(url);
			if (isCopiedToClipboard) {
				const message = `${this.translateService.instant('MODULE.JOB.LINK_CREATED_COPIED')}`;
				this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				
			} else {
				const snackBarRef =
					this.coreService.toastWithAction('Job Link Created', 'Copy Link');
				snackBarRef
					.onAction()
					.pipe(takeUntil(this.destroy))
					.subscribe(() => {
						isCopiedToClipboard = this.browser.copyTextToClipboard(url);
						if (!isCopiedToClipboard) {
							const message = `${this.translateService.instant('MODULE.JOB.LINK_CREATED_COPIED')}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						} else {
							const message = `${this.translateService.instant('MODULE.JOB.LINK_COPIED')}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						}
					});
			}
		}
	}
	
	private updateExistingJobLink(expiredLink: JobLink): void {
		const { generatedDate, expirationDate, key } = expiredLink;
		const generatedDateDisplay: string = this.convertAndFormatForDisplay(generatedDate);
		const expirationDateDisplay: string = this.convertAndFormatForDisplay(expirationDate);
		const index = this.jobLinks.findIndex(jobLink => jobLink.key === key);
		
		if (index >= 0) {
			const updatedLink = {
				...expiredLink,
				generatedDateDisplay,
				expirationDateDisplay,
			};
			updatedLink.status = this.getStatus(updatedLink) as any;
			this.jobLinks[index] = updatedLink;
			this.jobLinks = this.getSortedJobLinks(this.jobLinks);
		}
	}
	
	private toggleSlider() {
		this.jobService
			.toggleJobLinks(this.job.key)
			.pipe(takeUntil(this.destroy))
			.subscribe((active: boolean) => {
				// Sync local state with API response.
				this.linkActive = active;
				// Update the options' bitflag.
				if (active) {
					this.job.options &= ~JobOptions.LinksDisabled; // Remove flag
				} else {
					this.job.options |= JobOptions.LinksDisabled; // Add flag
				}

				// Recalculate all statuses
				this.jobLinks.forEach(link => {
					link.status = this.getStatus(link) as any;
				});
				this.jobLinks = this.getSortedJobLinks(this.jobLinks);

				const messageKey = active ? 'MODULE.JOB.LINK_ACTIVATED' : 'MODULE.JOB.LINK_DEACTIVATED';
				const message = `${this.translateService.instant(messageKey)}`;
				this.coreService.toast({ message: message, type: StatusType.SUCCESS });
			});
	}
	
	private getStatus(link: JobLink): SafeHtml {
		const now: number = Date.now();
		const expiration: number = this.parseAsUtc(link.expirationDate);
		const isActive: boolean = now < expiration && !this.job.areLinksDisabled;
		const color: string = isActive ? '#4caf50' : '#f44336';
		const text: string = isActive ? 'Active' : 'Inactive';
		const html: string = `<span style="color: ${color}; font-weight: 500;">${text}</span>`;
		
		return this.sanitizer.bypassSecurityTrustHtml(html);
	}
	
	private convertAndFormatForDisplay(date: Date | string): string | null {
		if (!date)
			return null;
		
		const dateStr = this.normalizeToUtc(date);
		const offsetString: string = this.timezoneService.offsetToTimezoneString(this.jobOffset);
		
		return formatInTimeZone(dateStr, offsetString, this.dateFormat);
	}
	
	private getSortedJobLinks(jobLinks: JobLink[]): JobLink[] {
		return orderBy([...jobLinks], ['generatedDate'], ['asc']);
	}
	
	private normalizeToUtc(date: Date | string): Date | string {
		return typeof date === 'string' && !date.endsWith('Z')
			? date + 'Z'
			: date;
	}
	
	private parseAsUtc(date: Date | string | null): number {
		if (!date) return Infinity;
		return new Date(this.normalizeToUtc(date)).getTime();
	}
}
